import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BedDouble, Bath, Ruler, MapPin, MountainSnow, ShieldCheck } from "lucide-react";
import { API } from "@/lib/api";
import axios from "axios";

import { Skeleton } from "@/components/ui/skeleton";

// Property prices are stored in SGD and shown to clients in CHF.
import { useSgdChfRate, sgdToChf } from "@/lib/currency";

export default function PublicShortlist() {
  const chfRate = useSgdChfRate();
  const fmt = (n) => sgdToChf(n || 0, chfRate);
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/public/shortlist/${token}`);
        setData(res.data);
      } catch (_e) { setError(true); }
    })();
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="text-center">
          <div className="label-eyebrow text-[#C5A880] mb-2">Alpine Straits</div>
          <div className="font-serif-display text-2xl text-[#0A1128]">This shortlist is no longer available.</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]" data-testid="public-shortlist-skeleton">
        <header className="bg-[#0A1128] text-white">
          <div className="max-w-6xl mx-auto px-8 py-10 space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <Skeleton className="w-9 h-9 rounded-md bg-[#152243]" />
              <div className="space-y-1">
                <Skeleton className="h-4 w-28 bg-slate-700" />
                <Skeleton className="h-3 w-20 bg-slate-800" />
              </div>
            </div>
            <Skeleton className="h-4 w-40 bg-slate-700" />
            <Skeleton className="h-12 w-80 bg-slate-700" />
            <Skeleton className="h-4 w-60 bg-slate-800" />
          </div>
        </header>
        <main className="max-w-6xl mx-auto px-8 py-12">
          <Skeleton className="h-4 w-32 mb-6" />
          <div className="grid md:grid-cols-2 gap-8">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="bg-white border border-[#E2E8F0] rounded-xl p-6 space-y-4">
                <Skeleton className="h-64 w-full rounded-lg" />
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <header className="bg-[#0A1128] text-white sidebar-noise">
        <div className="max-w-6xl mx-auto px-8 py-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-9 h-9 rounded-md bg-[#152243] border border-[#C5A880]/20 flex items-center justify-center">
              <MountainSnow className="w-5 h-5 text-[#C5A880]" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-serif-display text-base leading-none">Alpine Straits</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#C5A880] mt-1">Property Partners</div>
            </div>
          </div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#C5A880] mb-3">A private shortlist prepared for</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight">{data.lead_name}</h1>
          {data.lead_company && <div className="text-slate-300 mt-2">{data.lead_company}</div>}
          <div className="mt-6 flex items-center gap-2 text-sm text-slate-300 max-w-lg">
            <ShieldCheck className="w-4 h-4 text-[#C5A880]" />
            Singapore residential — <span className="text-white ml-1">0% Additional Buyer&apos;s Stamp Duty</span> for Swiss nationals under the Switzerland–Singapore FTA.
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-8 py-12">
        <div className="label-eyebrow mb-6">{data.properties.length} propert{data.properties.length === 1 ? "y" : "ies"} curated</div>
        {data.properties.length === 0 && <div className="text-slate-500">Your advisor will be adding properties shortly.</div>}
        <div className="grid md:grid-cols-2 gap-6">
          {data.properties.map((p) => (
            <div key={p.property_id} data-testid={`public-prop-${p.property_id}`} className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden hover-lift">
              <div className="h-56 bg-slate-100 relative overflow-hidden">
                {p.image_url && <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />}
                <div className="absolute top-3 left-3 bg-[#0A1128]/90 text-[#C5A880] text-[10px] tracking-widest uppercase px-2.5 py-1 rounded-full backdrop-blur">
                  ABSD saving ~{p.absd_savings_pct}%
                </div>
              </div>
              <div className="p-6">
                <div className="font-serif-display text-xl text-[#0A1128] leading-tight">{p.title}</div>
                <div className="flex items-center gap-1 text-xs text-slate-500 mt-1"><MapPin className="w-3 h-3" /> {p.district}</div>
                <div className="mt-3 flex items-center gap-5 text-xs text-slate-600">
                  <span className="flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" /> {p.bedrooms}</span>
                  <span className="flex items-center gap-1"><Bath className="w-3.5 h-3.5" /> {p.bathrooms}</span>
                  <span className="flex items-center gap-1"><Ruler className="w-3.5 h-3.5" /> {p.sqft} sqft</span>
                </div>
                <div className="mt-5 pt-5 border-t border-[#E2E8F0]">
                  <div className="label-eyebrow text-[10px]">Asking</div>
                  <div className="font-serif-display text-2xl text-[#0A1128]">{fmt(p.price_sgd)}</div>
                </div>
                {p.description && <p className="text-sm text-slate-600 mt-4 leading-relaxed">{p.description}</p>}
              </div>
            </div>
          ))}
        </div>
      </main>

      <footer className="border-t border-[#E2E8F0] bg-white mt-12">
        <div className="max-w-6xl mx-auto px-8 py-6 text-xs text-slate-500 flex items-center justify-between">
          <div>© {new Date().getFullYear()} Alpine Straits Property Partners · <a href="https://www.alpinestraitspartners.com.sg" target="_blank" rel="noopener noreferrer" className="hover:text-[#0A1128] underline underline-offset-2">alpinestraitspartners.com.sg</a> · Private &amp; Confidential</div>
          <div className="label-eyebrow text-[10px]">By invitation only</div>
        </div>
      </footer>
    </div>
  );
}
